/**
 * @fileoverview General Settings Page
 *
 * Purpose: Manage user profile (avatar, name, phone, signature) and account deletion.
 * URL: /dashboard/settings
 * Protection: Requires authentication
 *
 * Dependencies:
 * - Convex: users.queries.getCurrentUser, users.mutations.*
 * - Components: UserAvatar, SignatureCanvas, Tabs
 */

import { createFileRoute } from "@tanstack/react-router";
import { Upload, Pen, Type, Eraser, X, ImageUp } from "lucide-react";
import { useUploadFiles } from "@xixixao/uploadstuff/react";
import { Button, Input, Label, Slider, Switch } from "@/ui/primitives";
import { UserAvatar } from "@/ui/composed";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/ui/composed/sections";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "~/convex/_generated/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import type ReactSignatureCanvas from "react-signature-canvas";
import { toast } from "sonner";
import { removeSignatureBackground } from "@/utils/signature/removeSignatureBackground";

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

export const Route = createFileRoute("/_app/_auth/dashboard/_layout/settings/")(
  {
    component: DashboardSettings,
    beforeLoad: () => ({
      title: "Settings",
      headerTitle: "Settings",
      headerDescription: "Manage your account settings.",
    }),
  },
);

export default function DashboardSettings() {
  const { data: user } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const { mutateAsync: updateUserImage } = useMutation({
    mutationFn: useConvexMutation(api.users.mutations.updateUserImage),
  });
  const { mutateAsync: removeUserImage } = useMutation({
    mutationFn: useConvexMutation(api.users.mutations.removeUserImage),
  });
  const generateUploadUrl = useConvexMutation(
    api.users.mutations.generateUploadUrl,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { startUpload } = useUploadFiles(generateUploadUrl, {
    onUploadComplete: async (uploaded) => {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      await updateUserImage({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        imageId: (uploaded[0].response as any).storageId,
      });
    },
  });
  if (!user) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-6">
      {/* Avatar */}
      <div className={`flex w-full flex-col items-start ${GROUP_CARD_CLASS}`}>
        <div className="flex w-full items-start justify-between rounded-lg p-6">
          <div className="flex flex-col gap-2">
            <h2 className="text-xl font-medium text-primary">Your Avatar</h2>
            <p className="text-sm font-normal text-primary/60">
              This is your avatar. It will be displayed on your profile.
            </p>
          </div>
          <label
            htmlFor="avatar_field"
            className="group relative flex cursor-pointer overflow-hidden rounded-full transition active:scale-95"
          >
            <UserAvatar
              avatarUrl={user.avatarUrl}
              firstName={user.firstName}
              lastName={user.lastName}
              email={user.email}
              className="h-20 w-20"
              fallbackClassName="text-2xl"
            />
            <div className="absolute z-10 hidden h-full w-full items-center justify-center rounded-full bg-primary/40 group-hover:flex">
              <Upload className="h-6 w-6 text-secondary" />
            </div>
          </label>
          <input
            ref={fileInputRef}
            id="avatar_field"
            type="file"
            accept="image/*"
            className="peer sr-only"
            required
            tabIndex={user ? -1 : 0}
            onChange={async (event) => {
              if (!event.target.files) {
                return;
              }
              const files = Array.from(event.target.files);
              if (files.length === 0) {
                return;
              }
              startUpload(files);
            }}
          />
        </div>
        <div className="flex min-h-14 w-full items-center justify-between border-t border-border/50 bg-secondary/50 px-6">
          <p className="text-sm font-normal text-primary/60">
            Click on the avatar to upload a custom one from your files.
          </p>
          {user.avatarUrl && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => {
                removeUserImage({});
              }}
            >
              Reset
            </Button>
          )}
        </div>
      </div>

      {/* Name */}
      <ProfileNameCard
        firstName={user.firstName}
        lastName={user.lastName}
        phone={user.phone}
      />

      {/* Phone */}
      <PhoneCard phone={user.phone} />

      {/* Signature */}
      <SignatureCard
        signatureText={user.signatureText}
        signatureImageUrl={user.signatureImageUrl}
        signatureScale={user.signatureScale}
      />

      {/* Mail notifications (MOR-529) */}
      <MailNotificationsCard />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mail Notifications Card
// ---------------------------------------------------------------------------

/**
 * Global CC/forward opt-ins for job mailboxes (MOR-529). These are the
 * defaults applied to every job; a per-job override (set from a job's Mail
 * settings) wins over them. Toggling saves immediately since both flags are
 * sent together.
 */
function MailNotificationsCard() {
  const { data: prefs } = useQuery(
    convexQuery(api.mail.ccRules.getMyMailPrefs, {}),
  );
  const { mutateAsync: setGlobalMailPrefs } = useMutation({
    mutationFn: useConvexMutation(api.mail.ccRules.setGlobalMailPrefs),
  });
  const [saving, setSaving] = useState(false);

  const ccOutbound = prefs?.global?.ccOutbound ?? false;
  const forwardInbound = prefs?.global?.forwardInbound ?? false;

  const save = async (next: {
    ccOutbound: boolean;
    forwardInbound: boolean;
  }) => {
    setSaving(true);
    try {
      await setGlobalMailPrefs(next);
      toast.success("Mail notification settings updated.");
    } catch {
      toast.error("Failed to update mail settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`flex w-full flex-col ${GROUP_CARD_CLASS}`}>
      <div className="flex flex-col gap-4 p-6">
        <h2 className="text-xl font-medium text-primary">Mail</h2>
        <p className="text-sm font-normal text-primary/60">
          Defaults for every job mailbox. You can override these per job from
          the job's Mail settings.
        </p>
        <div className="flex max-w-lg items-start justify-between gap-4 rounded-lg border border-border/60 px-4 py-3">
          <div className="flex flex-col gap-0.5">
            <Label htmlFor="global-cc-outbound">CC me on outgoing mail</Label>
            <p className="text-xs text-primary/50">
              Added to the Cc of every email a job mailbox sends.
            </p>
          </div>
          <Switch
            id="global-cc-outbound"
            checked={ccOutbound}
            disabled={saving || prefs === undefined}
            onCheckedChange={(checked) =>
              void save({ ccOutbound: checked, forwardInbound })
            }
          />
        </div>
        <div className="flex max-w-lg items-start justify-between gap-4 rounded-lg border border-border/60 px-4 py-3">
          <div className="flex flex-col gap-0.5">
            <Label htmlFor="global-forward-inbound">
              Forward incoming mail to my inbox
            </Label>
            <p className="text-xs text-primary/50">
              Every inbound message is forwarded to your account email.
            </p>
          </div>
          <Switch
            id="global-forward-inbound"
            checked={forwardInbound}
            disabled={saving || prefs === undefined}
            onCheckedChange={(checked) =>
              void save({ ccOutbound, forwardInbound: checked })
            }
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Profile Name Card
// ---------------------------------------------------------------------------

function ProfileNameCard({
  firstName,
  lastName,
  phone,
}: {
  firstName?: string;
  lastName?: string;
  phone?: string;
}) {
  const [localFirst, setLocalFirst] = useState(firstName ?? "");
  const [localLast, setLocalLast] = useState(lastName ?? "");
  const [saving, setSaving] = useState(false);

  const { mutateAsync: updateProfile } = useMutation({
    mutationFn: useConvexMutation(api.users.mutations.updateProfile),
  });

  const isDirty =
    localFirst !== (firstName ?? "") || localLast !== (lastName ?? "");

  const handleSave = async () => {
    const trimmedFirst = localFirst.trim();
    const trimmedLast = localLast.trim();
    if (!trimmedFirst) {
      toast.error("First name is required.");
      return;
    }
    setSaving(true);
    try {
      await updateProfile({
        firstName: trimmedFirst || undefined,
        lastName: trimmedLast || undefined,
        phone,
      });
      toast.success("Name updated.");
    } catch {
      toast.error("Failed to update name.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`flex w-full flex-col ${GROUP_CARD_CLASS}`}>
      <div className="flex flex-col gap-4 p-6">
        <h2 className="text-xl font-medium text-primary">Your Name</h2>
        <p className="text-sm font-normal text-primary/60">
          This is how your name will appear across Morgan.
        </p>
        <div className="flex gap-4">
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="firstName">First Name</Label>
            <Input
              id="firstName"
              placeholder="First name"
              value={localFirst}
              onChange={(e) => setLocalFirst(e.target.value)}
              className="bg-transparent"
            />
          </div>
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="lastName">Last Name</Label>
            <Input
              id="lastName"
              placeholder="Last name"
              value={localLast}
              onChange={(e) => setLocalLast(e.target.value)}
              className="bg-transparent"
            />
          </div>
        </div>
      </div>
      <div className="flex min-h-14 w-full items-center justify-between border-t border-border/50 bg-secondary/50 px-6">
        <p className="text-sm font-normal text-primary/60">
          Please use your real name.
        </p>
        <Button size="sm" disabled={!isDirty || saving} onClick={handleSave}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phone Card
// ---------------------------------------------------------------------------

function PhoneCard({ phone }: { phone?: string }) {
  const [localPhone, setLocalPhone] = useState(phone ?? "");
  const [saving, setSaving] = useState(false);

  const { mutateAsync: updateProfile } = useMutation({
    mutationFn: useConvexMutation(api.users.mutations.updateProfile),
  });

  const isDirty = localPhone !== (phone ?? "");

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile({
        phone: localPhone.trim() || undefined,
      });
      toast.success("Phone number updated.");
    } catch {
      toast.error("Failed to update phone number.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`flex w-full flex-col ${GROUP_CARD_CLASS}`}>
      <div className="flex flex-col gap-4 p-6">
        <h2 className="text-xl font-medium text-primary">Phone Number</h2>
        <p className="text-sm font-normal text-primary/60">
          Your contact phone number.
        </p>
        <div className="flex max-w-sm flex-col gap-1.5">
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            type="tel"
            placeholder="+61 400 000 000"
            value={localPhone}
            onChange={(e) => setLocalPhone(e.target.value)}
            className="bg-transparent"
          />
        </div>
      </div>
      <div className="flex min-h-14 w-full items-center justify-between border-t border-border/50 bg-secondary/50 px-6">
        <p className="text-sm font-normal text-primary/60">
          Used for contact purposes only.
        </p>
        <Button size="sm" disabled={!isDirty || saving} onClick={handleSave}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Signature Card
// ---------------------------------------------------------------------------

function SignatureCard({
  signatureText,
  signatureImageUrl,
  signatureScale,
}: {
  signatureText?: string;
  signatureImageUrl?: string;
  signatureScale?: number;
}) {
  const hasSignature = !!(signatureText || signatureImageUrl);
  const [isEditing, setIsEditing] = useState(false);

  const showTabs = !hasSignature || isEditing;

  return (
    <div className={`flex w-full flex-col ${GROUP_CARD_CLASS}`}>
      <div className="flex flex-col gap-4 p-6">
        <h2 className="text-xl font-medium text-primary">Your Signature</h2>
        <p className="text-sm font-normal text-primary/60">
          Set a signature that can be used to sign documents.
        </p>

        {hasSignature && (
          <CurrentSignaturePreview
            signatureText={signatureText}
            signatureImageUrl={signatureImageUrl}
          />
        )}

        {/* Size preview + control — image signatures only; typed signatures
            render at a fixed font size (MOR-845). */}
        {signatureImageUrl && !isEditing && (
          <SignatureSizeSection
            signatureImageUrl={signatureImageUrl}
            signatureScale={signatureScale}
          />
        )}

        {hasSignature && !isEditing && (
          <Button
            variant="outline"
            size="sm"
            className="w-fit gap-1.5"
            onClick={() => setIsEditing(true)}
          >
            <Pen className="h-3.5 w-3.5" />
            Change Signature
          </Button>
        )}

        {showTabs && (
          <Tabs defaultValue="type" className="w-full">
            <TabsList className="w-full max-w-sm">
              <TabsTrigger value="type" className="flex-1 gap-1.5">
                <Type className="h-3.5 w-3.5" />
                Type
              </TabsTrigger>
              <TabsTrigger value="draw" className="flex-1 gap-1.5">
                <Pen className="h-3.5 w-3.5" />
                Draw
              </TabsTrigger>
              <TabsTrigger value="upload" className="flex-1 gap-1.5">
                <ImageUp className="h-3.5 w-3.5" />
                Upload
              </TabsTrigger>
            </TabsList>
            <TabsContent value="type">
              <TypedSignatureForm
                currentText={signatureText}
                onSaved={() => setIsEditing(false)}
              />
            </TabsContent>
            <TabsContent value="draw">
              <DrawnSignatureForm onSaved={() => setIsEditing(false)} />
            </TabsContent>
            <TabsContent value="upload">
              <UploadSignatureForm onSaved={() => setIsEditing(false)} />
            </TabsContent>
          </Tabs>
        )}
      </div>

      {hasSignature && <SignatureFooter />}
    </div>
  );
}

function CurrentSignaturePreview({
  signatureText,
  signatureImageUrl,
}: {
  signatureText?: string;
  signatureImageUrl?: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-medium uppercase tracking-wider text-primary/40">
        Current Signature
      </p>
      <div className="flex items-center justify-center rounded-lg border border-border/50 bg-white p-4 dark:bg-white/5">
        {signatureImageUrl ? (
          <img
            src={signatureImageUrl}
            alt="Your signature"
            className="max-h-20 object-contain dark:invert"
          />
        ) : signatureText ? (
          <p
            className="text-2xl text-gray-900 dark:text-gray-100"
            style={{ fontFamily: "'Caveat', cursive" }}
          >
            {signatureText}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Realistic size preview + slider for the signature as it appears on
 * generated documents (MOR-845). The preview mirrors the letter renderer's
 * bounds (60px × 240px at 100%), and the same multiplier scales the
 * signature stamped onto PDF forms (e.g. MYOB) — see
 * `convex/lib/signatureToken.ts`.
 */
function SignatureSizeSection({
  signatureImageUrl,
  signatureScale,
}: {
  signatureImageUrl: string;
  signatureScale?: number;
}) {
  const [scale, setScale] = useState(signatureScale ?? 1);
  const { mutateAsync: saveScale, isPending } = useMutation({
    mutationFn: useConvexMutation(api.users.mutations.updateSignatureScale),
  });

  const commit = async (value: number) => {
    try {
      await saveScale({ signatureScale: value });
      toast.success("Signature size saved");
    } catch {
      toast.error("Could not save the signature size");
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-medium uppercase tracking-wider text-primary/40">
        Size on documents
      </p>
      <div className="rounded-lg border border-border/50 bg-white p-5 dark:bg-white/5">
        {/* Mock sign-off block so the preview reflects how the signature
            actually sits on a generated letter or form. */}
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Yours faithfully,
        </p>
        <img
          src={signatureImageUrl}
          alt="Signature size preview"
          className="object-contain dark:invert"
          style={{
            maxHeight: `${Math.round(60 * scale)}px`,
            maxWidth: `${Math.round(240 * scale)}px`,
            margin: "4px 0",
          }}
        />
        <div className="h-px w-56 bg-gray-300 dark:bg-gray-600" />
        <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
          Signatory name
        </p>
      </div>
      <div className="flex max-w-sm items-center gap-3">
        <Slider
          value={[scale]}
          min={0.5}
          max={1.5}
          step={0.05}
          onValueChange={([v]) => setScale(v)}
          onValueCommit={([v]) => void commit(v)}
          disabled={isPending}
          aria-label="Signature size"
        />
        <span className="w-12 shrink-0 text-right text-xs tabular-nums text-primary/60">
          {Math.round(scale * 100)}%
        </span>
      </div>
      <p className="text-[11px] text-primary/50">
        Applies wherever your signature is stamped — letters and PDF forms
        alike.
      </p>
    </div>
  );
}

function TypedSignatureForm({
  currentText,
  onSaved,
}: {
  currentText?: string;
  onSaved?: () => void;
}) {
  const [text, setText] = useState(currentText ?? "");
  const [saving, setSaving] = useState(false);

  const { mutateAsync: updateSignature } = useMutation({
    mutationFn: useConvexMutation(api.users.mutations.updateSignature),
  });

  const isDirty = text.trim() !== (currentText ?? "");

  const handleSave = async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      toast.error("Please enter your signature text.");
      return;
    }
    setSaving(true);
    try {
      await updateSignature({ signatureText: trimmed });
      toast.success("Signature saved.");
      onSaved?.();
    } catch {
      toast.error("Failed to save signature.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 pt-2">
      <div className="flex max-w-md flex-col gap-1.5">
        <Label htmlFor="signatureText">Signature Text</Label>
        <Input
          id="signatureText"
          placeholder="e.g. John Smith"
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="bg-transparent"
        />
      </div>
      {text.trim() && (
        <div className="flex items-center justify-center rounded-lg border border-dashed border-border/50 bg-white p-4 dark:bg-white/5">
          <p
            className="text-2xl text-gray-900 dark:text-gray-100"
            style={{ fontFamily: "'Caveat', cursive" }}
          >
            {text}
          </p>
        </div>
      )}
      <div className="flex justify-end">
        <Button
          size="sm"
          disabled={!isDirty || saving || !text.trim()}
          onClick={handleSave}
        >
          {saving ? "Saving..." : "Save Typed Signature"}
        </Button>
      </div>
    </div>
  );
}

function DrawnSignatureForm({ onSaved }: { onSaved?: () => void }) {
  const sigRef = useRef<ReactSignatureCanvas | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [saving, setSaving] = useState(false);

  const generateUploadUrl = useConvexMutation(
    api.users.mutations.generateUploadUrl,
  );
  const { mutateAsync: updateSignature } = useMutation({
    mutationFn: useConvexMutation(api.users.mutations.updateSignature),
  });

  const { startUpload } = useUploadFiles(generateUploadUrl, {
    onUploadComplete: async (uploaded) => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const storageId = (uploaded[0].response as any).storageId;
        await updateSignature({ signatureImageId: storageId });
        toast.success("Signature saved.");
        sigRef.current?.clear();
        setIsEmpty(true);
        onSaved?.();
      } catch (err) {
        console.error("[Signature] updateSignature failed:", err);
        toast.error("Failed to save signature.");
      } finally {
        setSaving(false);
      }
    },
    onUploadError: (err) => {
      console.error("[Signature] Upload error:", err);
      toast.error("Failed to upload signature.");
      setSaving(false);
    },
  });

  const handleClear = useCallback(() => {
    sigRef.current?.clear();
    setIsEmpty(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error("Please draw your signature first.");
      return;
    }
    setSaving(true);
    try {
      const canvas = sigRef.current.getCanvas();
      const dataUrl = canvas.toDataURL("image/png");

      const byteString = atob(dataUrl.split(",")[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: "image/png" });
      const file = new File([blob], "signature.png", { type: "image/png" });
      startUpload([file]);
    } catch (err) {
      console.error("[Signature] Process error:", err);
      toast.error("Failed to process signature.");
      setSaving(false);
    }
  }, [startUpload]);

  return (
    <div className="flex flex-col gap-4 pt-2">
      <Label>Draw your signature</Label>
      <div className="overflow-hidden rounded-lg border border-border/50 bg-white dark:bg-white">
        <SignatureCanvas
          ref={sigRef}
          penColor="black"
          canvasProps={{
            className: "w-full h-40 cursor-crosshair",
            style: { width: "100%", height: "160px" },
          }}
          onBegin={() => setIsEmpty(false)}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={handleClear}
          disabled={isEmpty}
        >
          <Eraser className="mr-1.5 h-3.5 w-3.5" />
          Clear
        </Button>
        <Button size="sm" disabled={isEmpty || saving} onClick={handleSave}>
          {saving ? "Saving..." : "Save Drawn Signature"}
        </Button>
      </div>
    </div>
  );
}

function UploadSignatureForm({ onSaved }: { onSaved?: () => void }) {
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  // The background-stripped file actually uploaded (MOR-1033) — the raw
  // file input keeps the user's original selection for re-processing.
  const [processedFile, setProcessedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const generateUploadUrl = useConvexMutation(
    api.users.mutations.generateUploadUrl,
  );
  const { mutateAsync: updateSignature } = useMutation({
    mutationFn: useConvexMutation(api.users.mutations.updateSignature),
  });

  const { startUpload } = useUploadFiles(generateUploadUrl, {
    onUploadComplete: async (uploaded) => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const storageId = (uploaded[0].response as any).storageId;
        await updateSignature({ signatureImageId: storageId });
        toast.success("Signature uploaded.");
        setPreview(null);
        setProcessedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        onSaved?.();
      } catch {
        toast.error("Failed to save signature.");
      } finally {
        setSaving(false);
      }
    },
    onUploadError: () => {
      toast.error("Failed to upload signature.");
      setSaving(false);
    },
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Strip the paper background before anything else (MOR-1033) so the
    // preview shows exactly what will render on letters: ink only, no
    // white rectangle overlaying adjacent text.
    const cleaned = await removeSignatureBackground(file);
    setProcessedFile(cleaned);
    setPreview(URL.createObjectURL(cleaned));
  };

  const handleUpload = () => {
    const file = processedFile ?? fileInputRef.current?.files?.[0];
    if (!file) {
      toast.error("Please select a signature image first.");
      return;
    }
    setSaving(true);
    startUpload([file]);
  };

  return (
    <div className="flex flex-col gap-4 pt-2">
      <Label htmlFor="signatureUpload">Upload signature image</Label>
      <div
        className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-border/50 bg-secondary/30 p-6 transition-colors hover:border-primary/30 hover:bg-secondary/50"
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
        }}
        role="button"
        tabIndex={0}
      >
        {preview ? (
          <img
            src={preview}
            alt="Signature preview"
            className="max-h-24 object-contain"
          />
        ) : (
          <>
            <ImageUp className="h-8 w-8 text-primary/30" />
            <p className="text-sm text-primary/60">
              Click to select a PNG or JPG of your signature
            </p>
          </>
        )}
      </div>
      <input
        ref={fileInputRef}
        id="signatureUpload"
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="sr-only"
        onChange={handleFileChange}
      />
      <div className="flex justify-end gap-2">
        {preview && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setPreview(null);
              setProcessedFile(null);
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
          >
            Clear
          </Button>
        )}
        <Button size="sm" disabled={!preview || saving} onClick={handleUpload}>
          {saving ? "Uploading..." : "Save Uploaded Signature"}
        </Button>
      </div>
    </div>
  );
}

function SignatureFooter() {
  const [removing, setRemoving] = useState(false);

  const { mutateAsync: removeSignature } = useMutation({
    mutationFn: useConvexMutation(api.users.mutations.removeSignature),
  });

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await removeSignature({});
      toast.success("Signature removed.");
    } catch {
      toast.error("Failed to remove signature.");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="flex min-h-14 w-full items-center justify-between border-t border-border/50 bg-secondary/50 px-6">
      <p className="text-sm font-normal text-primary/60">
        Your signature will be used when signing documents.
      </p>
      <Button
        size="sm"
        variant="outline"
        onClick={handleRemove}
        disabled={removing}
        className="gap-1.5 text-destructive hover:bg-destructive/10 hover:text-destructive"
      >
        <X className="h-3.5 w-3.5" />
        {removing ? "Removing..." : "Remove Signature"}
      </Button>
    </div>
  );
}
